import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { GET_CUSTOMER_DATA } from '../queries/customerQueries';
import './Checkout.css';
import CheckoutSteps from './checkout/CheckoutSteps';
import ShippingAddressForm from './checkout/ShippingAddressForm';
import ShippingMethodsForm from './checkout/ShippingMethodsForm';
import PaymentMethodsForm from './checkout/PaymentMethodsForm';
import {
  GET_AVAILABLE_SHIPPING_METHODS,
  GET_AVAILABLE_PAYMENT_METHODS,
  GET_COUNTRIES,
  SET_SHIPPING_ADDRESS_AND_EMAIL,
  SET_SHIPPING_ADDRESS_FOR_CUSTOMER,
  SET_BILLING_ADDRESS,
  SET_SHIPPING_METHOD,
  SET_PAYMENT_METHOD,
  CREATE_PAYPAL_EXPRESS_TOKEN,
  GET_TOKENBASE_CHECKOUT_CONFIG,
  PLACE_ORDER,
  SYNC_AUTHNET_HOSTED_FORM
} from './checkout/checkoutQueries';
import {
  validateAddress,
  validateCardData,
  validateAuthorizeNetCardData,
  generateBraintreeNonce
} from './checkout/checkoutValidation';

// TODO надо сделать меньше шагов в чекауте (лучше всего одностраничный чекаут). На данный момент оставим такой чекаут
const Checkout = () => {
  const { cartData, cartId, loading: cartLoading, merging: cartMerging, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize step from URL hash or default to 1
  const getInitialStep = () => {
    const hash = location.hash.replace('#', '');
    const step = parseInt(hash) || 1;
    return step >= 1 && step <= 3 ? step : 1; // Max step is now 3 (no review step)
  };

  const [currentStep, setCurrentStep] = useState(getInitialStep());

  // Helper function to check if error is cart authorization or inactive cart error
  const isCartAuthError = (error) => {
    const isAuth = error?.message?.includes('cannot perform operations on cart') ||
                   error?.graphQLErrors?.some(err =>
                     err.extensions?.category === 'graphql-authorization'
                   );

    const isInactive = error?.message?.includes("cart isn't active") ||
                       error?.graphQLErrors?.some(err =>
                         err.extensions?.category === 'graphql-no-such-entity' ||
                         err.message?.includes("cart isn't active")
                       );

    return isAuth || isInactive;
  };

  // Update URL hash when step changes
  useEffect(() => {
    if (currentStep > 0) {
      navigate(`#${currentStep}`, { replace: true });
    }
  }, [currentStep, navigate]);

  // Handle browser back/forward navigation with hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const newStep = getInitialStep();
      if (newStep !== currentStep) {
        setCurrentStep(newStep);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [currentStep]);

  const [shippingAddress, setShippingAddress] = useState({
    firstname: '',
    lastname: '',
    street: [''],
    city: '',
    region: '',
    region_id: '',
    postcode: '',
    country_code: 'US',
    telephone: ''
  });
  const [email, setEmail] = useState('');
  const [billingAddress, setBillingAddress] = useState({
    firstname: '',
    lastname: '',
    street: [''],
    city: '',
    region: '',
    region_id: '',
    postcode: '',
    country_code: 'US',
    telephone: ''
  });
  const [useSameAsShipping, setUseSameAsShipping] = useState(true);
  const [selectedShipping, setSelectedShipping] = useState('');
  const [selectedPayment, setSelectedPayment] = useState('');
  const [shippingLoading, setShippingLoading] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [cardData, setCardData] = useState({
    number: '',
    expiryMonth: '',
    expiryYear: '',
    expiry: '', // For Authorize.Net MM/YY format
    cvv: '',
    holderName: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [acceptJsToken, setAcceptJsToken] = useState(null);
  const [showAddressSelector, setShowAddressSelector] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addressDataLoaded, setAddressDataLoaded] = useState(false);

  const [setShippingAddressAndEmailMutation] = useMutation(SET_SHIPPING_ADDRESS_AND_EMAIL);
  const [setShippingAddressForCustomerMutation] = useMutation(SET_SHIPPING_ADDRESS_FOR_CUSTOMER);
  const [setBillingAddressMutation] = useMutation(SET_BILLING_ADDRESS);
  const [setShippingMethodMutation] = useMutation(SET_SHIPPING_METHOD);
  const [setPaymentMethodMutation] = useMutation(SET_PAYMENT_METHOD);
  const [placeOrderMutation] = useMutation(PLACE_ORDER);
  const [syncHostedFormMutation] = useMutation(SYNC_AUTHNET_HOSTED_FORM);

  const { data: shippingMethodsData, loading: shippingMethodsLoading, refetch: refetchShippingMethods } = useQuery(GET_AVAILABLE_SHIPPING_METHODS, {
    variables: { cartId },
    skip: !cartId || currentStep < 2,
    fetchPolicy: 'network-only', // Changed from cache-first to ensure fresh data
    notifyOnNetworkStatusChange: false
  });

  const { data: paymentMethodsData, loading: paymentMethodsLoading } = useQuery(GET_AVAILABLE_PAYMENT_METHODS, {
    variables: { cartId },
    skip: !cartId || currentStep < 3,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false
  });

  const { data: countriesData } = useQuery(GET_COUNTRIES, {
    fetchPolicy: 'cache-first'
  });

  // Get customer data including addresses for logged-in users
  const { data: customerData } = useQuery(GET_CUSTOMER_DATA, {
    skip: !user,
    fetchPolicy: 'cache-first'
  });

  // Get TokenBase configuration for Authorize.Net
  const { data: tokenbaseConfigData } = useQuery(GET_TOKENBASE_CHECKOUT_CONFIG, {
    variables: { method: 'authnetcim' },
    skip: !paymentMethodsData?.cart?.available_payment_methods?.some(method => method.code === 'authnetcim'),
    fetchPolicy: 'cache-first'
  });

  // Get all countries
  const countries = countriesData?.countries || [];

  // Get regions for selected country
  const selectedCountryRegions = countriesData?.countries?.find(country =>
    country.two_letter_abbreviation === shippingAddress.country_code
  )?.available_regions || [];

  useEffect(() => {
    if (currentStep === 2 && shippingMethodsData) {
      const addressData = shippingMethodsData?.cart?.shipping_addresses?.[0];
      if (!addressData || !addressData.street || addressData.street.length === 0) {
        setErrors({
          general: 'Shipping address is missing. Please re-enter your shipping address.'
        });
        setCurrentStep(1);
      }
    }
  }, [currentStep, shippingMethodsData]);

  useEffect(() => {
    if (user && customerData?.customer && !addressDataLoaded) {
      if (customerData.customer.email) {
        setEmail(customerData.customer.email);
      }

      const addresses = customerData.customer.addresses || [];

      if (addresses.length > 1) {
        setShowAddressSelector(true);
      }

      const defaultShippingAddress = addresses.find(
        addr => addr.default_shipping
      ) || addresses[0];

      if (defaultShippingAddress) {
        setSelectedAddressId(defaultShippingAddress.id);
        setShippingAddress({
          firstname: defaultShippingAddress.firstname || '',
          lastname: defaultShippingAddress.lastname || '',
          street: defaultShippingAddress.street || [''],
          city: defaultShippingAddress.city || '',
          region: defaultShippingAddress.region?.region || '',
          region_id: defaultShippingAddress.region?.region_id || '',
          postcode: defaultShippingAddress.postcode || '',
          country_code: defaultShippingAddress.country_code || 'US',
          telephone: defaultShippingAddress.telephone || ''
        });
      } else if (addresses.length === 0) {
        setShippingAddress(prev => ({
          ...prev,
          firstname: customerData.customer.firstname || '',
          lastname: customerData.customer.lastname || ''
        }));
      }

      setAddressDataLoaded(true);
    }
  }, [user, customerData, addressDataLoaded]);

  if (cartLoading || cartMerging || !cartData) {
    return (
      <div className="checkout-container">
        <h1 className="checkout-title">Checkout</h1>
        <div className="loading-container">
          <div className="loading-spinner" />
          {cartMerging && (
            <p style={{ marginTop: '1rem', color: '#666', textAlign: 'center' }}>
              Updating your cart after login...
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!cartData.items && !cartLoading) {
    return (
      <div className="checkout-container">
        <h1 className="checkout-title">Checkout</h1>
        <div className="error-message" style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          padding: '1.5rem',
          textAlign: 'center',
          margin: '2rem 0'
        }}>
          <h2 style={{ color: '#c33', marginBottom: '1rem' }}>Cart Session Expired</h2>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            Your cart session has expired or is no longer accessible.
            A new cart is being created for you.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  if (!cartData.items || cartData.items.length === 0) {
    return (
      <div className="checkout-container">
        <h1 className="checkout-title">Checkout</h1>
        <div className="empty-cart-message">
          <h2>Cart is empty</h2>
        </div>
      </div>
    );
  }

  // Handle address selection from saved addresses
  const handleAddressSelect = (address) => {
    setSelectedAddressId(address.id);
    setShippingAddress({
      firstname: address.firstname || '',
      lastname: address.lastname || '',
      street: address.street || [''],
      city: address.city || '',
      region: address.region?.region || '',
      region_id: address.region?.region_id || '',
      postcode: address.postcode || '',
      country_code: address.country_code || 'US',
      telephone: address.telephone || ''
    });
    setIsEditingAddress(false);
  };

  // Toggle address editing mode
  const handleEditAddress = () => {
    setIsEditingAddress(true);
  };

  // Handle creating new address (clear form)
  const handleNewAddress = () => {
    setSelectedAddressId(null);
    setShippingAddress({
      firstname: '',
      lastname: '',
      street: [''],
      city: '',
      region: '',
      region_id: '',
      postcode: '',
      country_code: 'US',
      telephone: ''
    });
    setIsEditingAddress(true);
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    // Validate form
    const validationErrors = validateAddress(shippingAddress, email, billingAddress, useSameAsShipping);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    try {
      const addressPayload = {
        firstname: shippingAddress.firstname,
        lastname: shippingAddress.lastname,
        street: shippingAddress.street,
        city: shippingAddress.city,
        postcode: shippingAddress.postcode,
        country_code: shippingAddress.country_code,
        telephone: shippingAddress.telephone
      };

      // Only add region_id if it's selected, otherwise use region text
      if (shippingAddress.region_id) {
        addressPayload.region_id = shippingAddress.region_id;
      } else if (shippingAddress.region) {
        addressPayload.region = shippingAddress.region;
      }

      // Use different mutation based on whether user is logged in
      let result;
      if (user) {
        // For logged-in customers, don't set email (it's already associated with their account)
        result = await setShippingAddressForCustomerMutation({
          variables: {
            cartId,
            shippingAddress: addressPayload
          }
        });
      } else {
        // For guest users, set both email and shipping address
        result = await setShippingAddressAndEmailMutation({
          variables: {
            cartId,
            shippingAddress: addressPayload,
            email: email
          }
        });
      }

      // Set billing address based on user choice
      const billingAddressPayload = useSameAsShipping ? addressPayload : {
        firstname: billingAddress.firstname,
        lastname: billingAddress.lastname,
        street: billingAddress.street,
        city: billingAddress.city,
        postcode: billingAddress.postcode,
        country_code: billingAddress.country_code,
        telephone: billingAddress.telephone,
        ...(billingAddress.region_id ? { region_id: billingAddress.region_id } : { region: billingAddress.region })
      };

      await setBillingAddressMutation({
        variables: {
          cartId,
          billingAddress: billingAddressPayload,
          sameAsShipping: useSameAsShipping
        }
      });

      // Update shipping methods data directly from the mutation result
      const shippingAddressData = user
        ? result.data?.setShippingAddressesOnCart
        : result.data?.setShippingAddressesOnCart;

      const savedAddress = shippingAddressData?.cart?.shipping_addresses?.[0];
      if (!savedAddress || !savedAddress.street || savedAddress.street.length === 0) {
        setErrors({
          general: 'Failed to save shipping address. Please verify all fields are filled correctly and try again.'
        });
        setLoading(false);
        return;
      }

      if (!savedAddress.available_shipping_methods || savedAddress.available_shipping_methods.length === 0) {
        setErrors({
          general: 'No shipping methods available for this address. Please verify your address is correct.'
        });
        setLoading(false);
        return;
      }

      setCompletedSteps(prev => new Set(prev).add(1));
      setCurrentStep(2);
    } catch (error) {
      console.error('Error setting shipping address:', error);

      // Check for cart authorization errors
      if (isCartAuthError(error)) {
        console.log('Cart authorization error detected during address submission');
        // CartContext will handle creating a new cart, just redirect to home
        navigate('/', { replace: true });
        return;
      }

      if (error.graphQLErrors?.length > 0) {
        const errorMessage = error.graphQLErrors[0].message;
        setErrors({ general: errorMessage });
      } else {
        setErrors({ general: 'Failed to set shipping address. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShippingMethodSelect = (carrierCode, methodCode) => {
    const shippingKey = `${carrierCode}_${methodCode}`;
    const previousSelection = selectedShipping;
    setSelectedShipping(shippingKey);

    // If selection changed, remove completed steps after this one
    if (previousSelection !== shippingKey) {
      setCompletedSteps(prev => {
        const newSet = new Set(prev);
        newSet.delete(2);
        newSet.delete(3);
        return newSet;
      });
    }

    // Clear any previous shipping errors
    if (errors.shipping) {
      setErrors(prev => ({ ...prev, shipping: '' }));
    }
  };

  const handleShippingSubmit = async () => {
    if (!selectedShipping) {
      setErrors({ shipping: 'Please select a shipping method to continue.' });
      return;
    }

    // If step 2 is already completed and we have the same selection, just navigate
    if (completedSteps.has(2)) {
      setCurrentStep(3);
      return;
    }

    const [carrierCode, methodCode] = selectedShipping.split('_');
    setShippingLoading(true);

    try {
      await setShippingMethodMutation({
        variables: { cartId, carrierCode, methodCode }
      });
      setCompletedSteps(prev => new Set(prev).add(2));
      setCurrentStep(3);
    } catch (error) {
      console.error('Error setting shipping method:', error);

      // Check for cart authorization errors
      if (isCartAuthError(error)) {
        console.log('Cart authorization error detected during shipping method selection');
        navigate('/', { replace: true });
        return;
      }

      const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || '';
      if (errorMessage.includes('getStreetFull') || errorMessage.includes('Internal server error')) {
        setErrors({
          shipping: 'There was an issue with your shipping address. Please go back to step 1 and re-enter your address.'
        });
        setCurrentStep(1);
      } else {
        setErrors({
          shipping: errorMessage || 'Failed to select shipping method. Please try again.'
        });
      }
    } finally {
      setShippingLoading(false);
    }
  };

  const handlePaymentMethodSelect = (paymentCode) => {
    const previousSelection = selectedPayment;
    setSelectedPayment(paymentCode);

    // If selection changed, remove completed steps after this one
    if (previousSelection !== paymentCode) {
      setCompletedSteps(prev => {
        const newSet = new Set(prev);
        newSet.delete(3);
        return newSet;
      });
    }

    // Clear any previous payment errors
    if (errors.payment) {
      setErrors(prev => ({ ...prev, payment: '' }));
    }
  };

  const handlePaymentSubmit = async () => {
    if (!selectedPayment) {
      setErrors({ payment: 'Please select a payment method to continue.' });
      return;
    }

    // For Authorize.Net with iframe, payment is handled by the iframe component
    if (selectedPayment === 'authnetcim') {
      // The AuthorizeNetForm component will handle the iframe payment flow
      // This function will be called by the iframe's transaction completion callback
      return;
    }

    // Validate card data for credit card payments
    if (selectedPayment === 'braintree' || selectedPayment === 'braintree_cc_vault') {
      const cardValidationErrors = validateCardData(cardData);
      if (Object.keys(cardValidationErrors).length > 0) {
        setErrors(cardValidationErrors);
        return;
      }
    }

    // If step 3 is already completed and we have the same selection, just return
    if (completedSteps.has(3)) {
      return;
    }

    setPaymentLoading(true);
    setErrors({}); // Clear previous errors

    try {
      // Build payment method object based on the selected method
      let paymentMethodInput = { code: selectedPayment };

      // Add specific configuration for Braintree methods
      if (selectedPayment === 'braintree') {
        // Generate a payment method nonce using card data
        const paymentMethodNonce = await generateBraintreeNonce();

        if (!paymentMethodNonce) {
          setErrors({
            payment: 'Credit card payment is currently unavailable. Please select "Check / Money order" to complete your purchase.'
          });
          return;
        }

        paymentMethodInput = {
          code: selectedPayment,
          braintree: {
            payment_method_nonce: paymentMethodNonce,
            device_data: '', // Optional device data from Braintree
            is_active_payment_token_enabler: false
          }
        };
      } else if (selectedPayment === 'braintree_cc_vault') {
        // This payment method requires a saved card's public_hash
        // Since we don't have saved cards, show error and suggest alternatives
        setErrors({
          payment: 'Saved card payment is not available. Please select "Check / Money order" or enter new card details with "Credit Card" option.'
        });
        return;
      } else if (selectedPayment === 'checkmo') {
        // Check/Money Order doesn't need additional data
        paymentMethodInput = { code: selectedPayment };
      }

      await setPaymentMethodMutation({
        variables: {
          cartId,
          paymentMethod: paymentMethodInput
        }
      });
      setCompletedSteps(prev => new Set(prev).add(3));

      // Place order immediately after setting payment method
      console.log('Payment method set successfully, placing order immediately...');
      setOrderLoading(true);

      try {
        const orderResult = await placeOrderMutation({
          variables: { cartId }
        });

        const orderNumber = orderResult.data.placeOrder.order.order_number;
        console.log('Order placed successfully:', orderNumber);

        // Clear cart and create new empty cart
        await clearCart();

        // Redirect to success page with order number
        navigate('/checkout/success', {
          state: { orderNumber },
          replace: true
        });
      } catch (orderError) {
        console.error('Error placing order:', orderError);

        // Check for cart authorization errors
        if (isCartAuthError(orderError)) {
          console.log('Cart authorization error detected during order placement');
          navigate('/', { replace: true });
          return;
        }

        setErrors({
          order: 'Payment method was set successfully, but failed to place order. Please contact support.'
        });
        setOrderLoading(false);
      }
    } catch (error) {
      console.error('Error setting payment method:', error);
      console.error('Full error:', error.graphQLErrors || error.networkError || error);

      // Check for cart authorization errors
      if (isCartAuthError(error)) {
        console.log('Cart authorization error detected during payment method selection');
        navigate('/', { replace: true });
        return;
      }

      let errorMessage = 'Failed to select payment method. Please try again.';
      if (error.graphQLErrors?.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      }

      setErrors({ payment: errorMessage });
    } finally {
      setPaymentLoading(false);
    }
  };


  const handlePayPalSuccess = (paymentData) => {
    // For Magento PayPal, the success is handled by the return URL
    // The user will be redirected to PayPal and then back to our return URL
  };

  const handlePayPalError = (error) => {
    console.error('PayPal payment error:', error);
    setErrors({
      payment: 'PayPal payment failed. Please try again or select a different payment method.'
    });
  };

  const handlePayPalCancel = (data) => {
    setErrors({
      payment: 'PayPal payment was cancelled. Please try again or select a different payment method.'
    });
  };

  const handleAcceptJsToken = (token) => {
    setAcceptJsToken(token);
    // Clear any previous payment errors
    if (errors.payment) {
      setErrors(prev => ({ ...prev, payment: '' }));
    }
  };

  // Handle successful transaction from iframe
  const handleAuthorizeNetTransactionComplete = async (result) => {
    console.log('Authorize.Net transaction completed:', result);
    console.log('iframeSessionId from result:', result.iframeSessionId);

    try {
      setPaymentLoading(true);

      // Try to sync hosted form if we have session ID, otherwise use fallback
      let paymentMethodInput = { code: 'authnetcim' };

      if (result.iframeSessionId) {
        try {
          console.log('Syncing hosted form with Magento to get tokenized card data...');
          const syncResult = await syncHostedFormMutation({
            variables: {
              cartId,
              iframeSessionId: result.iframeSessionId,
              guestEmail: user?.email || shippingAddress?.email || null
            }
          });

          console.log('Sync completed successfully:', syncResult);

          // Extract the card data from sync result
          const cardData = syncResult.data?.authnetcimSyncHostedForm?.card;
          if (cardData) {
            // Use tokenized card data if available
            paymentMethodInput = {
              code: 'authnetcim',
              tokenbase_data: {
                id: cardData.id,
                type: cardData.type,
                label: cardData.label,
                selected: true
              }
            };
          }
        } catch (syncError) {
          console.warn('Sync failed, using basic payment method:', syncError);

          // Check for cart authorization errors in sync
          if (isCartAuthError(syncError)) {
            console.log('Cart authorization error detected during iframe sync');
            navigate('/', { replace: true });
            return;
          }

          // Continue with basic payment method for other errors
        }
      } else {
        console.warn('No iframeSessionId available, using transaction ID from iframe result');
        // Use the correct TokenBaseCardPaymentInput structure with transaction_id
        paymentMethodInput = {
          code: 'authnetcim',
          tokenbase_data: {
            transaction_id: result.transactionId
          }
        };
      }

      await setPaymentMethodMutation({
        variables: {
          cartId,
          paymentMethod: paymentMethodInput
        }
      });

      // Mark step as completed and automatically place order for iframe transactions
      setCompletedSteps(prev => new Set(prev).add(3));

      // For iframe transactions, the payment is already processed, so place the order immediately
      console.log('Iframe payment completed successfully, placing order automatically...');
      setOrderLoading(true);

      try {
        const orderResult = await placeOrderMutation({
          variables: { cartId }
        });

        const orderNumber = orderResult.data.placeOrder.order.order_number;
        console.log('Order placed successfully:', orderNumber);

        // Clear cart and create new empty cart
        await clearCart();

        // Redirect to success page with order number
        navigate('/checkout/success', {
          state: { orderNumber },
          replace: true
        });
      } catch (orderError) {
        console.error('Error placing order after iframe payment:', orderError);

        // Check for cart authorization errors
        if (isCartAuthError(orderError)) {
          console.log('Cart authorization error detected during iframe order placement');
          navigate('/', { replace: true });
          return;
        }

        setErrors({
          order: 'Payment was processed successfully, but failed to place order. Please contact support.'
        });
        setOrderLoading(false);
        // Payment was processed but order failed - stay on payment step
      }
    } catch (error) {
      console.error('Error setting payment method after iframe transaction:', error);

      // Check for cart authorization errors
      if (isCartAuthError(error)) {
        console.log('Cart authorization error detected during iframe payment method setting');
        navigate('/', { replace: true });
        return;
      }

      setErrors({
        payment: 'Transaction completed but failed to process in checkout. Please contact support.'
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  // Handle transaction error from iframe
  const handleAuthorizeNetTransactionError = (error) => {
    console.log('Authorize.Net transaction error:', error);
    setErrors({
      payment: error.error || 'Payment failed. Please try again.'
    });
    setPaymentLoading(false);
  };


  return (
    <div className="checkout-container">
      <h1 className="checkout-title">Checkout</h1>

      {/* Progress Steps */}
      <CheckoutSteps currentStep={currentStep} setCurrentStep={setCurrentStep} />

      {/* Step 1: Shipping Address */}
      {currentStep === 1 && (
        <ShippingAddressForm
          shippingAddress={shippingAddress}
          setShippingAddress={setShippingAddress}
          email={email}
          setEmail={setEmail}
          billingAddress={billingAddress}
          setBillingAddress={setBillingAddress}
          useSameAsShipping={useSameAsShipping}
          setUseSameAsShipping={setUseSameAsShipping}
          errors={errors}
          loading={loading}
          handleAddressSubmit={handleAddressSubmit}
          usStates={selectedCountryRegions}
          countries={countries}
          // Address selection props for logged-in customers
          isLoggedIn={!!user}
          customerAddresses={customerData?.customer?.addresses || []}
          showAddressSelector={showAddressSelector}
          isEditingAddress={isEditingAddress}
          selectedAddressId={selectedAddressId}
          onAddressSelect={handleAddressSelect}
          onEditAddress={handleEditAddress}
          onNewAddress={handleNewAddress}
        />
      )}

      {/* Step 2: Shipping Methods */}
      {currentStep === 2 && (
        <ShippingMethodsForm
          shippingMethodsData={shippingMethodsData}
          errors={errors}
          selectedShipping={selectedShipping}
          handleShippingMethodSelect={handleShippingMethodSelect}
          handleShippingSubmit={handleShippingSubmit}
          shippingLoading={shippingLoading}
          loading={shippingMethodsLoading}
        />
      )}

      {/* Step 3: Payment Methods */}
      {currentStep === 3 && (
        <PaymentMethodsForm
          paymentMethodsData={paymentMethodsData}
          errors={errors}
          selectedPayment={selectedPayment}
          handlePaymentMethodSelect={handlePaymentMethodSelect}
          handlePaymentSubmit={handlePaymentSubmit}
          paymentLoading={paymentLoading}
          cardData={cardData}
          setCardData={setCardData}
          loading={paymentMethodsLoading}
          cartId={cartId}
          cartData={cartData}
          handlePayPalSuccess={handlePayPalSuccess}
          handlePayPalError={handlePayPalError}
          handlePayPalCancel={handlePayPalCancel}
          onAcceptJsToken={handleAcceptJsToken}
          tokenbaseConfig={tokenbaseConfigData?.tokenBaseCheckoutConfig}
          onTransactionComplete={handleAuthorizeNetTransactionComplete}
          onTransactionError={handleAuthorizeNetTransactionError}
          guestEmail={user ? null : email}
        />
      )}

    </div>
  );
};

export default Checkout;